import { IManifest, Parser } from 'm3u8-parser';

import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { TEventBus } from '@eventBus/EventBus';
import { Fragment } from '@fragment/Fragment';
import { Logger, TLogger } from '@logger/Logger';
import { OpenObserveLogger } from '@logger/OpenObserveLogger';
import { IResolution } from '@resolutionsManager/IResolutionsManager';
import { ResolutionsManagerEvents } from '@resolutionsManager/ResolutionsManager.events';
import { extractEncodeTimestampFromExtraData } from '@streamLoader/helpers/frameExternalnfo';
import { ELoadingState } from '@streamLoader/net/INetworkLoader';
import { NetworkLoader } from '@streamLoader/net/NetworkLoader';
import bind from '@utils/decorators/bind';
import { IHydratedManifest, m3u8Hydrator } from '@utils/m3u8Hydrator/m3u8Hydrator';
import { IDoppioPlayerConfig } from 'config';

import { IBasePlaylistRotationControllerConfig } from './BasePlaylistRotationController.config';
import { BasePlaylistRotationControllerEvents } from './BasePlaylistRotationController.events';
import { LifecycleEventReporterFactory, TLifecycleEventReporter } from '../../Debugger/LifecycleEventReporter';
import { EDebuggableModuleState, IDebbugable, TDebuggableAdditionalInfo } from '../../types/common';

export abstract class BasePlaylistRotationController extends ConfigConsumer<IBasePlaylistRotationControllerConfig> implements IDebbugable {
  protected _lastPlaylistContent: string = '';
  protected _samePlaylistsCount: number = 0;
  protected _isActive: boolean = true;
  protected _currentPlaylistLoader: NetworkLoader | null = null;
  protected _url: string | null = null;

  protected readonly _logger: TLogger;
  protected readonly _lifecycleEventReporter: TLifecycleEventReporter;

  protected constructor(originalClassName: string, configKey: keyof IDoppioPlayerConfig, eventBus: TEventBus) {
    super(configKey, eventBus);

    this._logger = Logger(eventBus, `Doppio - ${originalClassName}`);
    this._lifecycleEventReporter = LifecycleEventReporterFactory('playlistRotator', this._eventBus);
    this._logger.info('Instance created');
  }

  public destroy(): void {
    super.destroy();

    this._isActive = false;
    this._logger.info('Destroyed');
  }

  protected abstract _loadNextPlaylist(callee?: string): void;

  protected abstract _planNextLoad(): void;

  @bind
  protected _onPlaylistLoadingStateChanged(prevState: ELoadingState, nextState: ELoadingState): void {
    if (!this._isActive) {
      this._logger.warn('Loader was destroyed, but we are trying to load handle playlist load state change!');
      return;
    }

    if (!this._currentPlaylistLoader || [ELoadingState.ABORTED, ELoadingState.ERROR].includes(nextState)) {
      this._lifecycleEventReporter(`Loading error reported, ${this._currentPlaylistLoader?.error}`);
      this._logger.warn(
        `Playlist loader reported ${nextState === ELoadingState.ERROR ? 'error' : 'as aborted'} (${
          this._currentPlaylistLoader?.error?.statusText || 'no error'
        } with status ${this._currentPlaylistLoader?.error?.statusText || 'no status'}). Planning next load`,
      );

      this._eventBus.publish(BasePlaylistRotationControllerEvents.playlists.playlistLoadingError, {
        error: this._currentPlaylistLoader?.error,
      });

      if (nextState === ELoadingState.ERROR && this._currentPlaylistLoader?.error?.status.toString()[0] === '4') {
        this._logger.warn('Playlist loading reported 4xx status. Requesting start over');

        this._eventBus.publish(ResolutionsManagerEvents.requestRestartFromMain, {});
      }

      this._currentPlaylistLoader = null;

      this._planNextLoad();

      return;
    }

    if (nextState !== ELoadingState.LOADED) {
      return;
    }

    if (!this._isPlaylistValid(this._currentPlaylistLoader.data as string)) {
      this._logger.warn('Playlist seems to be invalid. Plan next load');
      this._currentPlaylistLoader = null;

      this._planNextLoad();

      return;
    }

    this._eventBus.publish(BasePlaylistRotationControllerEvents.playlists.playlistLoaded, {
      originalUrl: this._url,
      realUrl: this._currentPlaylistLoader.realUrl,
      stats: this._currentPlaylistLoader.stats,
    });

    const data: string = this._currentPlaylistLoader.data as string;
    const isPlaylistChanged: boolean = this._isPlaylistChanged(data);

    this._currentPlaylistLoader = null;

    if (!isPlaylistChanged) {
      this._logger.warn('Loaded playlist is identical to previous one. Skipping it');

      this._planNextLoad();

      return;
    }

    const manifest: IHydratedManifest | null = this._parsePlaylist(data);

    if (!manifest) {
      this._logger.warn('Empty playlist parse result! Should restart');
      this._eventBus.publish(ResolutionsManagerEvents.requestRestartFromMain, {});
      return;
    }

    if (manifest.custom?.encodeTimestampMap) {
      this._eventBus.publish(BufferControllerEvents.latencyExtraInformationAvailable, manifest.custom.encodeTimestampMap);
    }

    this._eventBus.publish(BasePlaylistRotationControllerEvents.playlists.playlistInfo, manifest);

    this._lifecycleEventReporter('Playlist loaded');

    this._handleParsedPlaylist(manifest);
  }
  protected abstract _handleParsedPlaylist(manifest: IManifest): void;

  public abstract abortCurrentPlaylistLoading(): void;

  public abstract setURL(url: string, soft?: boolean): void;

  protected _isPlaylistValid(playlist: string): boolean {
    return Boolean(playlist?.trim().length) && playlist?.includes('#EXT-X-VERSION');
  }

  protected _isPlaylistChanged(data: string): boolean {
    return this._lastPlaylistContent !== data;
  }

  protected _isPlaylistUpToDate(data: string): boolean {
    if (this.config.equalPlaylistsAmountToDecline !== undefined && this.config.equalPlaylistsAmountToDecline < 1) {
      return true;
    }

    if (data === this._lastPlaylistContent) {
      this._samePlaylistsCount++;

      const { equalPlaylistsAmountToDecline = 2 } = this.config;

      if (this._samePlaylistsCount >= equalPlaylistsAmountToDecline) {
        return false;
      }
    } else {
      this._samePlaylistsCount = 0;
    }

    this._lastPlaylistContent = data;

    return true;
  }

  protected _parsePlaylist(data: string): IHydratedManifest | null {
    this._logger.verbose('Parsing manifest');
    try {
      const parser: Parser = new Parser();
      parser.addParser({
        expression: /^#EXT-X-MOUFLON:EXT-REF/,
        customType: 'encodeTimestampMap',
        dataParser: function (line: string) {
          return extractEncodeTimestampFromExtraData(line.replace('#EXT-X-MOUFLON:EXT-REF:', ''));
        },
      });
      parser.push(data);
      parser.end();

      const { manifest } = parser;

      this._logger.verbose('Manifest parsed');

      const hasPlaylists: boolean = Array.isArray(manifest?.playlists) && manifest.playlists.length > 0;
      const hasSegments: boolean = Array.isArray(manifest?.segments) && manifest.segments.length > 0;
      if (!hasPlaylists && !hasSegments) {
        OpenObserveLogger.reportError('Playlist seems to be parsed invalid', {
          playlistContent: data,
          playlistUrl: this._url,
          parsedPlaylistResult: manifest,
        });
      }

      return m3u8Hydrator(manifest, { partHoldBack: this.config.partHoldBack ? this.config.partHoldBack / 1000 : undefined });
    } catch (e) {
      OpenObserveLogger.reportError('Could not parse playlist', {
        playlistContent: data,
        playlistUrl: this._url,
      });
      return null;
    }
  }

  protected _isMultiVariantPlaylist(manifest: IManifest): boolean {
    return manifest.playlists && (!manifest.segments || (Array.isArray(manifest.segments) && manifest.segments.length === 0));
  }

  protected _isVODPlaylist(manifest: IManifest): boolean {
    return manifest.playlistType === 'VOD' || manifest.endList;
  }

  protected _isSimplePlaylist(manifest: IManifest): boolean {
    return !this._isLowLatencyPlaylist(manifest) && !this._isVODPlaylist(manifest);
  }

  protected _isLowLatencyPlaylist(manifest: IManifest): boolean {
    return Boolean(manifest.partInf);
  }

  protected _handleFragmentsDetected(fragments: Fragment[]): void {
    this._logger.verbose(
      'Detected fragments',
      fragments.map((fragment) => fragment.getMsnPn()),
    );
    this._logger.debug('Fragments was detected and extracted. Emitting it');
    this._lifecycleEventReporter(`Found fragments ${fragments.map(({ url }) => url).join(',')}`);

    this._eventBus.publish(BasePlaylistRotationControllerEvents.fragments.fragmentsDetected, fragments);
  }

  protected _handleLLManifestDetected(manifest: IManifest): void {
    this._logger.debug('Low-latency playlist detected. Emitting it');

    this._eventBus.publish(BasePlaylistRotationControllerEvents.playlistType.llDetected, manifest);
  }

  protected _handleVODPlaylist(manifest: IManifest): void {
    this._logger.debug('VOD playlist detected. Emitting it');

    this._eventBus.publish(BasePlaylistRotationControllerEvents.playlistType.vodDetected, manifest);
  }

  protected _handleMultivariantManifestDetected(manifest: IManifest): void {
    const variants: Record<string, IResolution> = manifest.playlists.reduce<Record<string, IResolution>>(
      (s, c) => {
        s[c.attributes.NAME] = {
          name: c.attributes.NAME,
          bitrate: c.attributes.BANDWIDTH,
          url: c.uri,
          width: c.attributes.RESOLUTION.width,
          height: c.attributes.RESOLUTION.height,
          fps: c.attributes['FRAME-RATE'],
        };
        return s;
      },
      {} as Record<string, IResolution>,
    );

    this._logger.verbose('Variants detected', variants);
    this._logger.debug('Multivariant playlist detected. Emitting it');

    this._eventBus.publish(BasePlaylistRotationControllerEvents.playlistType.multivariantDetected, variants);
  }

  protected _handleSimplePlaylistDetected(manifest: IManifest): void {
    this._logger.debug('Simple playlist detected. Emitting it');

    this._eventBus.publish(BasePlaylistRotationControllerEvents.playlistType.simpleDetected, manifest);
  }

  abstract get state(): EDebuggableModuleState;

  abstract get status(): string;

  abstract get additionalInfo(): TDebuggableAdditionalInfo;
}
